import { Toaster } from 'sonner';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import '@/App.css';

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Layout from '@/components/Layout';
import Dashboard from '@/pages/Dashboard';
import PermitsList from '@/pages/PermitsList';
import PermitNew from '@/pages/PermitNew';
import PermitDetail from '@/pages/PermitDetail';
import Sites from '@/pages/Sites';
import Users from '@/pages/Users';
import Help from '@/pages/Help';
import Bugs from '@/pages/Bugs';
import BriefingMode from '@/pages/BriefingMode';
import Notifications from '@/pages/Notifications';
import Contacts from '@/pages/Contacts';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#DA291C]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/permits" element={<ProtectedRoute><PermitsList /></ProtectedRoute>} />
      <Route path="/permits/new" element={<ProtectedRoute><PermitNew /></ProtectedRoute>} />
      <Route path="/permits/:id" element={<ProtectedRoute><PermitDetail /></ProtectedRoute>} />
      <Route path="/permits/:id/briefing" element={<ProtectedRoute><BriefingMode /></ProtectedRoute>} />
      <Route path="/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
      <Route path="/bugs" element={<ProtectedRoute><Bugs /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
